import { useState, useEffect } from "react";
import { LoginForm } from "@/components/Auth/LoginForm";
import { SignUpForm } from "@/components/Auth/SignUpForm";
import { SellerDashboard } from "@/components/Seller/SellerDashboard";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";

// Login page with role-based authentication

const Login = () => {
  const [user, setUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<"customer" | "weaver" | null>(null);
  const [showSignUp, setShowSignUp] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we're on the weaver dashboard route
  const isSellerDashboardRoute = location.pathname === "/seller/dashboard";

  useEffect(() => {
    // Check for existing session
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.user) {
        setUser(session.user);
        
        // Get user role
        const { data: roleData } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', session.user.id)
          .single();
        
        const role = roleData?.role as "customer" | "weaver";
        setUserRole(role);

        // Redirect based on role
        if (role === 'customer') {
          navigate('/designer');
        }
        // If weaver, stay on login page to show dashboard
      }
    };

    checkSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (session?.user) {
          setUser(session.user);
          
          const { data: roleData } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', session.user.id)
            .single();
          
          const role = roleData?.role as "customer" | "weaver";
          setUserRole(role);

          if (role === 'customer') {
            navigate('/designer');
          }
        } else {
          setUser(null);
          setUserRole(null);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleLogin = async (role: "user" | "seller", credentials: any) => {
    // Authentication is handled in LoginForm component
    // This is just for updating local state after successful login
    if (role === "user") {
      navigate("/designer");
    }
    // If weaver, stay on this page to show dashboard
  };

  const handleSignUp = (role: "user" | "seller", credentials: any) => {
    // Authentication is handled in SignUpForm component
    // This is just for updating local state after successful signup
    setShowSignUp(false);
    
    if (role === "user") {
      navigate("/designer");
    }
    // If weaver, they'll be redirected to dashboard after signup
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setUserRole(null);
  };

  const handleShowSignUp = () => {
    setShowSignUp(true);
  };

  const handleBackToLogin = () => {
    setShowSignUp(false);
  };

  // If user is logged in as weaver, show dashboard
  if (user && userRole === "weaver") {
    return <SellerDashboard onLogout={handleLogout} />;
  }

  // Show sign-up form if requested
  if (showSignUp) {
    return (
      <SignUpForm
        onSignUp={handleSignUp}
        onBackToLogin={handleBackToLogin}
        defaultTab={isSellerDashboardRoute ? "seller" : "user"}
      />
    );
  }

  // If on weaver dashboard route but not logged in, show login form with weaver tab active
  if (isSellerDashboardRoute) {
    return (
      <LoginForm
        onLogin={handleLogin}
        onShowSignUp={handleShowSignUp}
        defaultTab="seller"
      />
    );
  }

  // Otherwise show login form
  return (
    <LoginForm
      onLogin={handleLogin}
      onShowSignUp={handleShowSignUp}
    />
  );
};

export default Login;
